import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Video, Upload, X, Loader2 } from 'lucide-react';
import { lotesService } from '../../services/lotesService';
import { comprimirVideo, TAMANHO_MINIMO_COMPRIMIR } from '../../utils/comprimirVideo';

const MAX_BYTES = 250 * 1024 * 1024; // 250MB — limite do arquivo enviado (depois da compressão)
const MAX_ORIGINAL_BYTES = 1024 * 1024 * 1024; // 1GB — aceito só se a compressão no navegador funcionar
const TIPOS_ACEITOS = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];

// Gera uma capa (thumbnail) a partir do próprio arquivo de vídeo, direto no navegador —
// não passa pelo backend nem precisa de processamento de vídeo no servidor.
function gerarPosterBlob(file) {
  return new Promise((resolve) => {
    let resolvido = false;
    const finalizar = (blob) => {
      if (resolvido) return;
      resolvido = true;
      resolve(blob || null);
    };

    try {
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';
      videoEl.muted = true;
      videoEl.playsInline = true;
      const objectUrl = URL.createObjectURL(file);
      videoEl.src = objectUrl;

      const limpar = () => URL.revokeObjectURL(objectUrl);

      videoEl.addEventListener('loadedmetadata', () => {
        videoEl.currentTime = Math.min(1, (videoEl.duration || 1) / 2);
      });
      videoEl.addEventListener('seeked', () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = videoEl.videoWidth || 320;
          canvas.height = videoEl.videoHeight || 180;
          canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => { limpar(); finalizar(blob); }, 'image/jpeg', 0.75);
        } catch {
          limpar();
          finalizar(null);
        }
      });
      videoEl.addEventListener('error', () => { limpar(); finalizar(null); });
      setTimeout(() => { limpar(); finalizar(null); }, 8000);
    } catch {
      finalizar(null);
    }
  });
}

export default function VideoUploadLote({ organizadorId, leilaoId, loteId, videoUrl, posterUrl, onChange, disabled }) {
  const inputRef = useRef(null);
  const [enviando, setEnviando] = useState(false);
  const [etapa, setEtapa] = useState('');
  const [progresso, setProgresso] = useState(0);
  const [erro, setErro] = useState('');

  const modoPendente = !loteId;

  const handleSelecionar = () => inputRef.current?.click();

  const enviarArquivo = async (kind, blob, filename, contentType, onProgress) => {
    const { upload_url, fields, key } = modoPendente
      ? await lotesService.presignVideoLotePendente(organizadorId, leilaoId, { kind, filename, content_type: contentType, file_size: blob.size })
      : await lotesService.presignVideoLote(organizadorId, loteId, { kind, filename, content_type: contentType, file_size: blob.size });

    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => form.append(k, v));
    form.append('file', blob);

    await axios.post(upload_url, form, {
      onUploadProgress: onProgress ? (evt) => {
        if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
      } : undefined,
    });

    if (modoPendente) {
      const { url } = await lotesService.confirmarVideoLotePendente(organizadorId, leilaoId, key, kind);
      return { url, pendingKey: key };
    }
    const resultado = await lotesService.confirmarVideoLote(organizadorId, loteId, key, kind);
    return { url: kind === 'poster' ? resultado.video_poster_url : resultado.video_url, pendingKey: null };
  };

  const handleArquivo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setErro('');

    if (!TIPOS_ACEITOS.includes(file.type)) {
      setErro('Formato não suportado. Use MP4, MOV, WEBM ou AVI.');
      return;
    }
    if (file.size > MAX_ORIGINAL_BYTES) {
      setErro('Vídeo excede o limite de 1GB.');
      return;
    }

    setEnviando(true);
    setProgresso(0);
    try {
      // Comprime no navegador (720p, índice no início) — upload mais rápido e vídeo que
      // toca logo no catálogo. Se não der, envia o original (respeitando o limite de 250MB).
      let arquivoEnvio = file;
      let nomeEnvio = file.name;
      let tipoEnvio = file.type;
      if (file.size >= TAMANHO_MINIMO_COMPRIMIR) {
        setEtapa('Otimizando vídeo...');
        const comprimido = await comprimirVideo(file, setProgresso);
        if (comprimido) {
          arquivoEnvio = comprimido;
          nomeEnvio = file.name.replace(/\.[^.]+$/, '') + '.mp4';
          tipoEnvio = 'video/mp4';
        }
        setProgresso(0);
      }
      if (arquivoEnvio.size > MAX_BYTES) {
        setErro('Vídeo excede o limite de 250MB.');
        return;
      }

      setEtapa('Enviando vídeo...');
      const video = await enviarArquivo('video', arquivoEnvio, nomeEnvio, tipoEnvio, setProgresso);
      onChange?.({ video_url: video.url, video_pending_key: video.pendingKey });

      setEtapa('Gerando capa...');
      setProgresso(0);
      const posterBlob = await gerarPosterBlob(file);
      if (posterBlob) {
        const poster = await enviarArquivo('poster', posterBlob, 'poster.jpg', 'image/jpeg');
        onChange?.({ video_poster_url: poster.url, poster_pending_key: poster.pendingKey });
      }
    } catch (err) {
      console.error('Erro ao enviar vídeo:', err);
      setErro(err.response?.data?.detail || 'Falha ao enviar o vídeo. Tente novamente.');
    } finally {
      setEnviando(false);
      setEtapa('');
      setProgresso(0);
    }
  };

  const handleRemover = async () => {
    if (!window.confirm('Remover o vídeo deste lote?')) return;
    try {
      if (!modoPendente) {
        await lotesService.removerVideoLote(organizadorId, loteId);
      }
      onChange?.({ video_url: null, video_poster_url: null, video_pending_key: null, poster_pending_key: null });
    } catch (err) {
      console.error('Erro ao remover vídeo:', err);
      setErro('Falha ao remover o vídeo.');
    }
  };

  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
        <Video size={16} className="text-red-500" />
        Vídeo do Lote (upload, até 1GB — otimizado automaticamente)
      </label>

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
        className="hidden"
        onChange={handleArquivo}
        disabled={disabled || enviando}
      />

      {videoUrl ? (
        <div className="flex items-center gap-3">
          <video src={videoUrl} poster={posterUrl || undefined} controls preload="none" className="w-40 h-24 rounded-lg bg-black object-contain" />
          {!disabled && (
            <button
              type="button"
              onClick={handleRemover}
              className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
            >
              <X size={16} /> Remover vídeo
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleSelecionar}
          disabled={disabled || enviando}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg text-sm ${
            disabled || enviando ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
          }`}
        >
          {enviando ? (
            <>
              <Loader2 size={16} className="animate-spin" /> {etapa} {etapa === 'Enviando vídeo...' ? `${progresso}%` : ''}
            </>
          ) : (
            <>
              <Upload size={16} /> Selecionar vídeo (MP4, MOV, WEBM, AVI — máx. 1GB)
            </>
          )}
        </button>
      )}

      {erro && <p className="text-sm text-red-600 mt-1">{erro}</p>}
    </div>
  );
}
